import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CatalogDTO,
  FolderType,
  PlatformLocale,
  ProcessResponseDTOAssessmentType,
  useGetProcessById,
  useGetTestCatalog
} from '@/api/generated';

const EMPTY_CATALOG: { catalog: CatalogDTO[] } = { catalog: [] };

interface UseGetProcessTestsResult {
  tests: CatalogDTO[];
  assessmentType?: ProcessResponseDTOAssessmentType;
  isLoading: boolean;
  error: string;
  refetch: () => void;
}

export const useGetProcessTests = (processId: string): UseGetProcessTestsResult => {
  const {
    i18n: { language: currentLocale }
  } = useTranslation();
  const { data: process, isLoading: isProcessLoading, error: processError, refetch } = useGetProcessById(processId);
  const {
    data: { catalog } = EMPTY_CATALOG,
    isLoading: isCatalogLoading,
    error: catalogError
  } = useGetTestCatalog(
    {
      assessmentType: process?.assessmentType,
      locale: currentLocale as PlatformLocale,
      location_slug: process?.location?.slug as string,
      location_type: process?.location?.type as FolderType
    },
    { query: { enabled: !!process?.assessmentType && !!process?.location?.slug } }
  );

  const tests = useMemo(() => {
    const processItemsSet = new Set(process?.catalogItems);

    return catalog?.filter(({ catalogId }) => processItemsSet.has(catalogId!)) || [];
  }, [process, catalog]);

  const isLoading = isProcessLoading || isCatalogLoading;

  const error = [processError, catalogError]
    .filter(Boolean)
    .map(error => (error as { message: string })?.message || (error as string))
    .join('\n');

  return { assessmentType: process?.assessmentType, tests, isLoading, error, refetch };
};
